import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

const SingleSelectDropdown = ({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionValue,
  loading = false,
  helperText,
  disabled = false,
  placeholder = 'Search...',
  size = 'medium',
  minWidth = 240,
}) => {
  const selectedOption = useMemo(
    () => options.find((option) => String(getOptionValue(option)) === String(value)) || null,
    [options, value, getOptionValue]
  );

  return (
    <Box sx={{ minWidth }}>
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={(_, nextOption) => onChange(nextOption ? String(getOptionValue(nextOption)) : '')}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, selected) =>
          String(getOptionValue(option)) === String(getOptionValue(selected))
        }
        disabled={disabled}
        loading={loading}
        clearOnEscape
        renderInput={(params) => (
          <TextField
            {...params}
            label={label || undefined}
            placeholder={placeholder}
            helperText={helperText}
            size={size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </Box>
  );
};

SingleSelectDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  loading: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SingleSelectDropdown.defaultProps = {
  label: '',
  value: '',
  getOptionLabel: (option) => option.label ?? option.name ?? '',
  getOptionValue: (option) => option.value ?? option._id ?? option.id,
  loading: false,
  helperText: undefined,
  disabled: false,
  placeholder: 'Search...',
  size: 'medium',
  minWidth: 240,
};

export default SingleSelectDropdown;
